import axios from 'axios';
import type { DemoStatusResponse, MyWeekResponse, AssignmentCheckResult, ScenarioLever, RollupRootSearchResult, RollupRootJqlSearchResult, PersonLever, PersonScenarioResponse } from '../types';
import { refreshAccessToken } from './sessionManager';
import { useAuthStore } from '../store/useAuthStore';

// A request timeout is a hard safety net: without one, a request that hangs
// (e.g. a read waiting on a DB connection held by a heavy background job) never
// resolves OR rejects, leaving pages stuck on "Loading…" forever. 60s is well
// above any normal read/write; truly long operations can override per-call.
const api = axios.create({ baseURL: '/api', timeout: 60_000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('erm-token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Pre-auth endpoints — flows where the user isn't authenticated yet OR is
// recovering credentials. A 401 from any of these means "validation failure
// / wrong credentials / email-exists-with-different-password", NOT "session
// expired". Force-logging-out and redirecting to /login from these would
// wipe the page state the form needs to show its error UI (signup modal,
// invalid-token panel, etc.). User report 2026-05-24: signup with an
// existing email caused a forced redirect to /login, taking the user back
// to the signup form with all fields cleared, looking like an "infinite
// reload" loop.
// Exported for testing — pinned by lib/__tests__/api.test.ts so a future
// edit can't accidentally drop an exempted endpoint and regress signup.
export const PRE_AUTH_URL_RE = /\/auth\/(login|register|org\/create|org\/request-access|invite\/|forgot-password|reset-password|verify-email|resend-verification)/;

// Session-expiry recovery (2026-07-02, the idle-timeout system): a 401 on an
// authenticated endpoint first attempts ONE silent token refresh (the httpOnly
// session cookie authenticates it — see lib/sessionManager.ts) and replays the
// request. Only a FAILED refresh — the session is genuinely dead (idle-expired,
// revoked, past its 7-day cap, or a legacy pre-session token) — force-logs-out.
// A 401 on the replay is NOT a logout: the session is demonstrably alive, so
// it's the endpoint's own semantics (e.g. change-password's "current password
// is incorrect"), and the caller's error UI should handle it. Impersonation
// skips refresh entirely — the cookie belongs to the operator's session, and
// refreshing mid-impersonation would silently swap identities back.
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const config = err.config ?? {};
    const url = config.url ?? '';
    const isPreAuthRequest = PRE_AUTH_URL_RE.test(url);
    if (err.response?.status === 401 && !isPreAuthRequest && !config.__sessionRetry) {
      const canRefresh = !useAuthStore.getState().isImpersonating && !!localStorage.getItem('erm-token');
      const fresh = canRefresh ? await refreshAccessToken() : null;
      if (fresh) {
        config.__sessionRetry = true;
        // The request interceptor re-reads erm-token (already updated by the
        // refresh) — just replay.
        return api(config);
      }
      localStorage.removeItem('erm-token');
      localStorage.removeItem('erm-user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// Auth
// SOC 2 audit-log viewer (read-only, paginated; org-scoped server-side)
export const auditApi = {
  list: (params: Record<string, string | number>) => api.get('/audit-log', { params }),
};

// Avium API for Enterprise · Phase 2 — outbound webhook management (Enterprise).
// Human-managed admin surface → /api/admin/webhooks (JWT), NOT the /v1 machine
// namespace (which is API-key-only). Mirrors the api-keys management route.
export const webhooksApi = {
  eventTypes: () => api.get('/admin/webhooks/event-types'),
  list: () => api.get('/admin/webhooks'),
  create: (body: { url: string; events: string[]; description?: string }) => api.post('/admin/webhooks', body),
  update: (id: string, body: Record<string, unknown>) => api.patch(`/admin/webhooks/${id}`, body),
  remove: (id: string) => api.delete(`/admin/webhooks/${id}`),
  rotateSecret: (id: string) => api.post(`/admin/webhooks/${id}/rotate-secret`),
  deliveries: (id: string, limit = 50) => api.get(`/admin/webhooks/${id}/deliveries`, { params: { limit } }),
  redeliver: (id: string, deliveryId: string) => api.post(`/admin/webhooks/${id}/deliveries/${deliveryId}/redeliver`),
  ping: (id: string) => api.post(`/admin/webhooks/${id}/ping`),
};

export const authApi = {
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),
  createOrg: (name: string, email: string, password: string, orgName: string, founding = false, linkedin = '') =>
    api.post('/auth/org/create', { name, email, password, orgName, founding, linkedin }),
  requestAccess: (name: string, email: string, password: string, orgId: string) =>
    api.post('/auth/org/request-access', { name, email, password, orgId }),
  selectOrg: (orgId: string) => api.post('/auth/org/select', { orgId }),
  // Create another workspace for the already-authenticated user — backs
  // the "+ Create new workspace" entry on the OrgSelectorPage so the user
  // can spin up another org without retyping email/password.
  createAnotherOrg: (orgName: string) => api.post('/auth/orgs', { orgName }),
  listOrgs: (search: string) => api.get('/auth/orgs', { params: { search } }),
  getInvite: (token: string) => api.get(`/auth/invite/${token}`),
  acceptInvite: (token: string, name: string, password: string) =>
    api.post(`/auth/invite/${token}/accept`, { name, password }),
  verifyEmail: (token: string) => api.post('/auth/verify-email', { token }),
  resendVerification: (email: string) => api.post('/auth/resend-verification', { email }),
  markWelcomeSeen:  () => api.post('/auth/welcome-seen'),
  resetWelcome:     () => api.post('/auth/welcome-reset'),
  markTourComplete: () => api.post('/auth/tour-complete'),
  // Existing-user privacy acceptance gate. Records consent to the CURRENT policy
  // for the signed-in user (new signups accept at account creation instead). The
  // server stamps its own current version, so no version is sent from here.
  acceptPrivacy:    () => api.post('/auth/accept-privacy'),
  setPreferredView: (preferredView: 'leader' | 'scrum_master' | 'ic') =>
    api.post('/auth/preferred-view', { preferredView }),
  setPersona: (persona: string | null) => api.post('/auth/persona', { persona }),
  setPreferredScope: (scope: { teamIds?: string[]; departmentIds?: string[] }) =>
    api.post('/auth/preferred-scope', scope),
  // Update the signed-in user's profile photo. avatarUrl = cropped data URL,
  // or null to remove it. Returns { avatarUrl }.
  updateProfile: (avatarUrl: string | null) =>
    api.put('/auth/profile', { avatarUrl }),
};

// The capability contract, readable by EVERY role (RULE 16) — what works, what's one
// confirmation away, what's off by choice. Feeds the post-sync banner and any surface
// explaining why a capability is dark. Changing the contract stays behind /admin.
export const capabilityApi = {
  contract: (source = 'jira') => api.get(`/capability-contract?source=${source}`),
};

// Public contact form — no auth required, posted from the sign-in page so a
// prospect can reach the team without an account.
export const contactApi = {
  submit: (data: { name: string; email: string; company?: string; message: string }) =>
    api.post('/contact', data),
};

// Public Enterprise inquiry — structured fields, routes to sales triage email.
export const enterpriseInquiryApi = {
  submit: (data: {
    name: string;
    email: string;
    jobTitle?: string;
    company: string;
    teamSize: string;
    procurement?: string[];
    timeline?: string;
    message?: string;
  }) => api.post('/enterprise-inquiry', data),
};

// In-app issue reporter — authenticated, accepts an optional screenshot.
// Identity (name/email/org) is filled in server-side from the session so we
// don't have to trust (or rely on) the client to send it.
export const reportIssueApi = {
  submit: (data: {
    subject?: string;
    message: string;
    pageUrl?: string;
    screenshot?: { filename: string; contentBase64: string; mimeType: string };
  }) => api.post('/report-issue', data),
};

// Org member management
export const orgApi = {
  me: () => api.get('/org/me'),
  plan: () => api.get('/org/plan'),
  // Cross-org admin overview — every org the current user is admin of,
  // with member count + isCurrent flag. Backs the "My Organizations"
  // admin page.
  myAdminOrgs: () => api.get('/org/my-admin-orgs'),
  setMetricUnit: (metricUnit: 'hours' | 'points') => api.put('/org/settings', { metricUnit }),
  // Workload tidy-up window (days; 0 = never hide, null = product default 14).
  setStaleDoneDays: (staleDoneDays: number | null) => api.put('/org/settings', { staleDoneDays }),
  // Planning milestone — which date the risk/forecast engine plans against, plus
  // the date-typed source fields the admin can pick for the custom-date option.
  milestoneSettings: () => api.get('/org/settings/milestone'),
  setPlanningMilestone: (data: { planningMilestoneType: string; planningMilestoneFieldId?: string | null }) =>
    api.put('/org/settings', data),
  // Delivery-analytics mode — 'auto' (infer from the milestone), 'sprint', or
  // 'kanban'. Picks which risk engine judges the org (sprint forecast vs Kanban
  // flow health). Read alongside milestoneSettings().
  setDeliveryMode: (deliveryMode: 'auto' | 'sprint' | 'kanban') =>
    api.put('/org/settings', { deliveryMode }),
  // Kanban SLE override (days) — null/0 reverts to the auto-derived 85th pctile.
  setSleTarget: (flowSleTargetDays: number | null) =>
    api.put('/org/settings', { flowSleTargetDays }),
  // Kanban WIP limits (Phase 2) — per in-progress status column.
  wipLimits: () => api.get('/org/wip-limits'),
  setWipLimit: (statusId: string, limit: number | null) =>
    api.put('/org/wip-limits', { statusId, limit }),
  branding: () => api.get('/org/branding'),
  members: () => api.get('/org/members'),
  invites: () => api.get('/org/invites'),
  invite: (email: string, role: string, personalMessage?: string) =>
    api.post('/org/members/invite', { email, role, ...(personalMessage ? { personalMessage } : {}) }),
  inviteLink: (id: string) => api.get(`/org/invites/${id}/link`),
  revokeInvite: (id: string) => api.delete(`/org/invites/${id}`),
  /** Cascade delete — purge ALL invite records for an email AND remove the
   *  matching org member if any. User row stays (preserves other-org access). */
  purgeByEmail: (email: string) => api.post(`/org/invites/purge-by-email`, { email }),
  approve: (userId: string) => api.put(`/org/members/${userId}/approve`),
  deny: (userId: string) => api.put(`/org/members/${userId}/deny`),
  setRole: (userId: string, role: string) => api.put(`/org/members/${userId}/role`, { role }),
  remove: (userId: string) => api.delete(`/org/members/${userId}`),
};

// Notification preferences (per-user, default off) + "Send me a sample" for any
// scheduled email (daily briefing, weekly digest, sprint-ending, blocked-spike)
export const notificationsApi = {
  preferences: () => api.get('/notifications/preferences'),
  setPreference: (type: string, enabled: boolean) => api.put('/notifications/preferences', { type, enabled }),
  // Which weekdays a scheduled email may send ('Sun'…'Sat' keys, ≥1 required)
  setSendDays: (type: string, sendDays: string[]) => api.put('/notifications/preferences', { type, sendDays }),
  setTimezone: (timezone: string) => api.put('/notifications/timezone', { timezone }),
  sendBriefingPreview: (type?: string) => api.post('/notifications/briefing/preview', type ? { type } : {}),
  setBriefingSettings: (settings: { depth?: string; quietWhenClean?: boolean; sendHour?: number | null; followedTeamIds?: string[]; automationHourlyCap?: number | null }) =>
    api.put('/notifications/briefing-settings', settings),
};

// Resources
export const resourcesApi = {
  list: (params?: Record<string, string>) => api.get('/resources', { params }),
  get: (id: string) => api.get(`/resources/${id}`),
  create: (data: Record<string, unknown>) => api.post('/resources', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/resources/${id}`, data),
  delete: (id: string) => api.delete(`/resources/${id}`),
  timeLogs: (id: string, params?: Record<string, string>) => api.get(`/resources/${id}/timelogs`, { params }),
  // THE STANDARD DAY (AVM-386) — a person's working rate, effective-dated so
  // editing it never rewrites what their capacity WAS. WORK-tier, like every
  // other planning write: the dated, attributed history is the control.
  getStandardDay: (id: string) => api.get(`/resources/${id}/standard-day`),
  setStandardDay: (id: string, data: { hoursPerDay: number; effectiveFrom?: string; note?: string }) =>
    api.put(`/resources/${id}/standard-day`, data),
};

// Absences (the PTO-painting layer behind the Universal Capacity Editor's
// day-strip, Capacity Holistic Redesign state E). One row per person, per
// calendar day; 0 hours deletes the row (server/src/routes/absences.ts).
export const absencesApi = {
  list: (resourceId: string, params?: { from?: string; to?: string }) =>
    api.get(`/resources/${resourceId}/absences`, { params }),
  // `hours: null` is a points-org "day out" marker with no clock-hours amount —
  // never a fabricated placeholder (2026-07-18 follow-through, Law 1).
  // `stagedAbsence` (2026-07-20, the user-staged-lane resolution) is set ONLY
  // by CapacityMakeItRealSheet's hypo_absence line, on the LAST of its N
  // per-day calls — the server mints exactly ONE staged ActionItem row per
  // move from it. Every other caller (e.g. the Universal Capacity Editor)
  // omits it and is unaffected.
  set: (resourceId: string, date: string, data: { hours: number | null; kind?: string; note?: string; stagedAbsence?: { totalDays: number } }) =>
    api.put(`/resources/${resourceId}/absences/${date}`, data),
};

// My Week (Capacity Holistic Redesign, state F) — the per-person source-
// adaptive week surface. weekStart defaults server-side to the current
// calendar week's Monday.
export const myWeekApi = {
  get: (weekStart?: string) => api.get<MyWeekResponse>('/my-week', { params: weekStart ? { weekStart } : {} }),
};

// Time Logs
export const timeLogsApi = {
  list: (params?: Record<string, string>) => api.get('/timelogs', { params }),
  create: (data: Record<string, unknown>) => api.post('/timelogs', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/timelogs/${id}`, data),
  delete: (id: string) => api.delete(`/timelogs/${id}`),
};

// Tour Steps (admin-editable guided product tour content)
export const tourStepsApi = {
  list:            () => api.get('/tour-steps'),
  listAll:         () => api.get('/tour-steps/all'),
  create:          (data: { target?: string | null; placement?: string | null; route?: string | null; title: string; body: string; order?: number }) =>
    api.post('/tour-steps', data),
  update:          (id: string, data: Record<string, unknown>) => api.put(`/tour-steps/${id}`, data),
  delete:          (id: string) => api.delete(`/tour-steps/${id}`),
  reorder:         (ids: string[]) => api.put('/tour-steps/reorder', { ids }),
  restoreDefaults: () => api.post('/tour-steps/restore-defaults'),
};

// Avium Signal catalog — per-org enable/disable + relabel (Phase 2a).
export const signalCatalogApi = {
  list:            () => api.get('/signal-configs'),
  update:          (signalType: string, data: { enabled?: boolean; label?: string | null }) =>
    api.put(`/signal-configs/${signalType}`, data),
  reset:           (signalType: string) => api.delete(`/signal-configs/${signalType}`),
  restoreDefaults: () => api.post('/signal-configs/restore-defaults'),
};

// Timer Sessions (live start/stop time tracking)
export const timersApi = {
  active: (ticketId: string) => api.get('/timers/active', { params: { ticketId } }),
  autoStopped: (ticketId: string, resourceId: string) =>
    api.get('/timers/auto-stopped', { params: { ticketId, resourceId } }),
  start: (ticketId: string, resourceId: string) =>
    api.post('/timers/start', { ticketId, resourceId }),
  stop: (id: string) => api.post(`/timers/${id}/stop`),
  acknowledgeWarning: (id: string) => api.post(`/timers/${id}/acknowledge-warning`),
  dismiss: (id: string) => api.post(`/timers/${id}/dismiss`),
};

// Tickets
export const ticketsApi = {
  list: (params?: Record<string, string>) => api.get('/tickets', { params }),
  // THE WALK (RULE 26). Decomposes a work item's effort into its own + everything beneath
  // it, with a link from every contributing row back to the ticket in Jira / Azure DevOps /
  // Asana. This is what makes it legal to show a rolled-up number at all — a total the
  // customer cannot walk back to their own rows is a claim, not a number.
  effortRollup: (id: string) => api.get(`/tickets/${id}/effort-rollup`),
  // The customer's OWN source fields on this item (Region, Customer, …), from data already
  // ingested into rawFields — shown at last on the ticket (AVM-275 #5, read-only).
  customFields: (id: string) => api.get<{ ticketId: string; key: string; fields: Array<{ fieldId: string; name: string; type: string | null; value: string }> }>(`/tickets/${id}/custom-fields`),
  // THE ROLLUP SURFACE (AVM-245) — the whole forest, three metrics deep, for the
  // dedicated /rollup page. Page-level companion to effortRollup (one epic); the
  // single-ticket panel stays untouched. `rootKeys` (ticket KEYS — the query-entered
  // picker's committed selection, AVM-051) scopes to several parents' subtrees combined;
  // omitted → the whole org (still used by any legacy caller).
  rollupTree: (rootKeys?: string[]) =>
    api.get('/rollup/tree', { params: rootKeys && rootKeys.length ? { rootKeys: rootKeys.join(',') } : {} }),
  // THE ARRIVAL PICKER's search-and-check candidate list (AVM-051) — plausible rollup
  // roots (has children, or reads as an epic/feature/initiative), server-searched and
  // honestly paginated (never silently capped, Law 1).
  rollupEpicSearch: (q: string, limit?: number, offset?: number) =>
    api.get<RollupRootSearchResult>('/rollup/epics', {
      params: { q, ...(limit != null ? { limit } : {}), ...(offset != null ? { offset } : {}) },
    }),
  // THE JQL POWER LANE (AVM-051 Concept 4 / Work Center slice W5, Jira-first) — a sibling mode on
  // the SAME endpoint: the practitioner's own JQL, run against their live Jira, resolved to local
  // synced epics. Jira's own rejection text (and the "no Jira connection" reject) come back as a
  // 400 the caller reads off the error response, not a thrown-away generic failure.
  rollupEpicJqlSearch: (jql: string, limit?: number) =>
    api.get<RollupRootJqlSearchResult>('/rollup/epics', {
      params: { jql, ...(limit != null ? { limit } : {}) },
    }),
  // Import Refresh Model 1.0 — soft-archived tickets (a "full replace" import
  // reconciled them out). List + one-click restore; permanent delete is the
  // existing admin remove(). Team+ (importContinuity) — the server 403s below.
  archived: () => api.get('/tickets/archived'),
  restore: (id: string) => api.post(`/tickets/${id}/restore`),
  // Paged variant (AVM-148 scroll-first board): same endpoint with ?limit /
  // ?offset — the response becomes { tickets, total, staleHidden, hasMore }
  // instead of the bare array.
  listPage: (params: Record<string, string>) => api.get('/tickets', { params }),
  // DB-side aggregates for every number the Workload used to derive from the
  // full download: per-status counts/sums, per-source counts, out-of-sync.
  boardSummary: (params?: Record<string, string>) => api.get('/tickets/board-summary', { params }),
  // Distinct project/release/sprint combos for the filter dropdown cascade —
  // never the full ticket payload.
  cascade: (params?: Record<string, string>) => api.get('/tickets', { params: { ...(params ?? {}), view: 'cascade' } }),
  get: (id: string) => api.get(`/tickets/${id}`),
  // Resolve by KEY (e.g. AV-215) — the Reports drill path, where payloads
  // carry the human handle rather than the DB id. Same shape as get().
  getByKey: (key: string) => api.get(`/tickets/by-key/${encodeURIComponent(key)}`),
  create: (data: Record<string, unknown>) => api.post('/tickets', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/tickets/${id}`, data),
  delete: (id: string) => api.delete(`/tickets/${id}`),
  addResource: (id: string, resourceId: string, role?: string) =>
    api.post(`/tickets/${id}/resources`, { resourceId, role }),
  // The write side of a Scenario `move_ticket` lever's "Make it real"
  // (2026-07-19 adversarial-review fix) — reshapes ONLY `fromResourceId`'s
  // slice of this ticket's attribution to `toResourceId` (allocations +
  // ticketResources + the assignee fields only when the ticket's current
  // owner resolves to `from`), so what Scenario mode PREVIEWED is exactly
  // what this commits. See server/src/lib/plannedEffortWriter.ts's
  // reassignPersonAttribution for the queue-semantics note. `assigneeChanged`
  // is true exactly when the write moved the ticket's owner; `queuedForPush`
  // (DECIDED 2026-07-20, AVM-348 decision (b)) is true only when that also
  // joined the connector's out-of-sync push queue — a partial slice move
  // (assigneeChanged false) never queues, since it has nothing Jira/Azure/
  // Asana can receive.
  reassignAttribution: (id: string, data: { fromResourceId: string; toResourceId: string }) =>
    api.post<{
      ticketId: string;
      ticketKey: string;
      fromResourceId: string;
      toResourceId: string;
      allocationsMoved: number;
      ticketResourcesMoved: number;
      assigneeChanged: boolean;
      queuedForPush: boolean;
    }>(`/tickets/${id}/reassign-attribution`, data),
  removeResource: (id: string, resourceId: string) =>
    api.delete(`/tickets/${id}/resources/${resourceId}`),
  updatePhase: (id: string, statusId: string | null) => api.patch(`/tickets/${id}/phase`, { statusId }),
  // AVM-167 "correct the record" — assert (or clear) a completion date by hand.
  // Avium-owned + done-stage tickets only; audited server-side; the ticket
  // carries completedAtSource='asserted' so provenance stays visible.
  assertCompletion: (id: string, completedAt: string | null) => api.patch(`/tickets/${id}/completion`, { completedAt }),
  updateRanks: (ranks: { id: string; rank: number }[]) => api.put('/tickets/ranks', { ranks }),
  // Bulk field edit from the Workload board's multi-select — reassign · move
  // sprint · re-prioritize in one call. Returns { updated, queuedForPush, field }.
  bulkUpdate: (ticketIds: string[], field: 'assignee' | 'sprint' | 'priority', value: string | null) =>
    api.put('/tickets/bulk', { ticketIds, field, value }),
  // One-click Undo for a bulk edit — restores each ticket's prior value (the
  // `before` array returned by bulkUpdate).
  bulkRevert: (field: 'assignee' | 'sprint' | 'priority', items: { id: string; value: string | null }[]) =>
    api.put('/tickets/bulk-revert', { field, items }),
  parentCandidates: (projectKey: string, excludeKey?: string, q?: string) =>
    api.get('/tickets/parent-candidates', { params: { projectKey, ...(excludeKey ? { excludeKey } : {}), ...(q ? { q } : {}) } }),
  hierarchyParentCandidates: (hierarchyId: string, childLevelId: string, excludeKey?: string, q?: string) =>
    api.get('/tickets/hierarchy-parent-candidates', { params: { hierarchyId, childLevelId, ...(excludeKey ? { excludeKey } : {}), ...(q ? { q } : {}) } }),
  searchTickets: (q: string, excludeKey?: string) =>
    api.get('/tickets/search', { params: { q, ...(excludeKey ? { excludeKey } : {}) } }),
};

// Projects — the unified SourceProject concept (imported + Avium-native). Reads
// are open to any member; create/update/archive/delete are admin-only server-side.
// (Distinct from the snapshot feature still mounted at /projects.)
export const projectsApi = {
  list: (includeArchived?: boolean) => api.get('/source-projects', { params: includeArchived ? { includeArchived: 'true' } : {} }),
  create: (data: { name: string; description?: string }) => api.post('/source-projects', data),
  update: (id: string, data: { name?: string; description?: string; archived?: boolean }) => api.patch(`/source-projects/${id}`, data),
  remove: (id: string) => api.delete(`/source-projects/${id}`),
};

// Avium Files — org document storage (folders, upload, preview, download).
export const filesApi = {
  status:  () => api.get('/files/status'),
  folders: () => api.get('/files/folders'),
  createFolder: (name: string, parentId?: string | null) => api.post('/files/folders', { name, parentId }),
  updateFolder: (id: string, data: { name?: string; parentId?: string | null }) => api.put(`/files/folders/${id}`, data),
  deleteFolder: (id: string) => api.delete(`/files/folders/${id}`),
  list: (folderId?: string | null) => api.get('/files', { params: folderId ? { folderId } : {} }),
  upload: (file: File, folderId: string | null, onProgress?: (pct: number) => void) => {
    const form = new FormData();
    form.append('file', file);
    if (folderId) form.append('folderId', folderId);
    return api.post('/files/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: e => { if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
    });
  },
  fileUrl: (id: string, mode: 'preview' | 'download') => api.get(`/files/${id}/url`, { params: { mode } }),
  previewHtml: (id: string) => api.get(`/files/${id}/preview-html`),
  update: (id: string, data: { name?: string; folderId?: string | null }) => api.put(`/files/${id}`, data),
  delete: (id: string) => api.delete(`/files/${id}`),
};

// Document → Stories: upload a doc, review extracted action items, commit as
// Avium Stories (See the work). /analyze never writes; /commit creates tickets.
export const docImportApi = {
  analyze: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/doc-import/analyze', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  commit: (payload: { fileName: string; source: string; projectId?: string; items: Array<{ title: string; sections: Array<{ heading: string; lines: string[] }> }> }) =>
    api.post('/doc-import/commit', payload),
  history: () => api.get('/doc-import/history'),
};

// Ticket comments (Avium-native discussion thread). Routes mount at /api:
// list/create are ticket-scoped; edit/delete are comment-scoped.
export const commentsApi = {
  list:   (ticketId: string) => api.get(`/tickets/${ticketId}/comments`),
  create: (ticketId: string, body: string, mentionUserIds?: string[]) =>
    api.post(`/tickets/${ticketId}/comments`, { body, ...(mentionUserIds?.length ? { mentionUserIds } : {}) }),
  update: (commentId: string, body: string, mentionUserIds?: string[]) =>
    api.patch(`/comments/${commentId}`, { body, ...(mentionUserIds?.length ? { mentionUserIds } : {}) }),
  delete: (commentId: string) => api.delete(`/comments/${commentId}`),
  // Approved org members for the @-mention autocomplete (id/name/avatarUrl only).
  mentionableUsers: () => api.get('/comments/mentionable-users'),
};

// Super Admin
export const superAdminApi = {
  listUsers:  () => api.get('/super-admin/users'),
  listOrgs:   () => api.get('/super-admin/orgs'),
  createOrg:  (orgName: string) => api.post('/super-admin/orgs', { orgName }),
  enterOrg:   (orgId: string) => api.post(`/super-admin/orgs/${orgId}/enter`),
  deleteOrg:  (orgId: string) => api.delete(`/super-admin/orgs/${orgId}`),
  deleteUser: (userId: string) => api.delete(`/super-admin/users/${userId}`),
  impersonate: (userId: string) => api.post(`/super-admin/impersonate/${userId}`),
  encryptTokens: () => api.post('/super-admin/encrypt-tokens'),
  updateOrgPlan: (orgId: string, data: {
    plan?: string;
    seatLimit?: number | null;
    planOverride?: boolean;
    overrideNotes?: string;
    trialEndsAt?: string | null;
    foundingMember?: boolean;
    foundingLinkedin?: string;
  }) => api.put(`/super-admin/orgs/${orgId}/plan`, data),
  // Phase-0 telemetry snapshot (per-route latency p50/p95/max + recent slow
  // queries + memory) — readable on prod by super-admins via the Support Console.
  metrics: () => api.get<MetricsSnapshot>('/super-admin/metrics'),
  // Deep-dive: run the real Brief engine for an org + return whether it completes,
  // its timing, and the actual signal breakdown (triages a "0 signals" Brief).
  intelDebug: (orgId: string) => api.get<Record<string, unknown>>(`/super-admin/orgs/${orgId}/intel-debug`),
  // Email usage ledger rollup (month + today vs the Resend plan limits,
  // itemized by category → type). Mirrors server lib/emailUsage.ts.
  emailUsage: () => api.get<EmailUsage>('/super-admin/email-usage'),
};

// ── Email usage (super-admin Email usage tab) ────────────────────────────────
// Mirrors the server shapes in lib/emailUsage.ts.

export interface EmailUsageCounts { sent: number; failed: number }
export interface EmailUsageTypeRow { type: string; year: EmailUsageCounts; month: EmailUsageCounts; today: EmailUsageCounts }
export interface EmailUsageCategoryRow { category: string; year: EmailUsageCounts; month: EmailUsageCounts; today: EmailUsageCounts; types: EmailUsageTypeRow[] }
export interface EmailUsageOrgCategoryRow { category: string; year: EmailUsageCounts; month: EmailUsageCounts; today: EmailUsageCounts; types: EmailUsageTypeRow[] }
export interface EmailUsageOrgRow {
  orgId: string | null; // null = the Platform bucket (org-less + pre-attribution sends)
  name: string;
  year: EmailUsageCounts;
  month: EmailUsageCounts;
  today: EmailUsageCounts;
  categories: EmailUsageOrgCategoryRow[]; // full category → type ladder for the org drill-in
}
export interface EmailUsage {
  timezone: 'UTC';
  generatedAt: string;
  yearStart: string;
  monthStart: string;
  dayStart: string;
  limits: { monthly: number; daily: number };
  year: EmailUsageCounts; // informational — Resend's quota is monthly
  month: EmailUsageCounts;
  today: EmailUsageCounts;
  categories: EmailUsageCategoryRow[];
  organizations: EmailUsageOrgRow[]; // EVERY organization (zeros included) by month volume; Platform bucket pinned last
  daily: { date: string; sent: number; failed: number }[];
  monthly: { month: string; sent: number; failed: number }[];
  trackingSince: string | null;
  orgTrackingSince: string | null; // where workspace attribution honestly begins
}

// ── Marketing Hub (super-admin email campaigns) ──────────────────────────────
// Mirrors the server shapes in routes/marketing.ts + lib/marketingEmail.ts.

export type MarketingBlock =
  | { type: 'hero'; headline: string; sub?: string }
  | { type: 'image'; url: string; alt: string; caption?: string }
  | { type: 'paragraph'; text: string }
  | { type: 'valueProps'; items: { title: string; detail: string }[] }
  | { type: 'cta'; label: string; url: string }
  | { type: 'contact'; heading?: string; note?: string };

export type MarketingAudience = 'admins' | 'users' | 'all';
export type MarketingCampaignStatus = 'draft' | 'scheduled' | 'sending' | 'paused' | 'sent';

export interface MarketingCampaignStats {
  recipients: number;
  pending: number;
  sent: number;
  failed: number;
  skippedUnsubscribed: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  unsubscribed: number;
}

export interface MarketingCampaign {
  id: string;
  name: string;
  subject: string;
  previewText: string | null;
  // Masthead band overrides (null = the default "From the Avium team" band).
  mastheadTitle: string | null;
  mastheadCadence: string | null;
  mastheadIcon: string | null;
  bodyJson: string;
  audience: MarketingAudience;
  orgIds: string[];          // empty = every org
  includeContacts: boolean;  // external contact list opted in for this campaign
  recipientEmails: string[]; // non-empty = hand-picked mode (exact addresses)
  status: MarketingCampaignStatus;
  scheduledFor: string | null;
  links: string[];
  createdById: string;
  sendStartedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  stats?: MarketingCampaignStats;
}

export interface MarketingAudienceCounts {
  admins: number;
  users: number;
  all: number;
  suppressedUnsubscribed: number;
  contacts: number;           // eligible external contacts (deduped vs accounts)
  contactsSuppressed: number; // unsubscribed contacts on the rolling list
  recipients: number;         // the selection's true total (groups + contacts if opted in)
}

// The targeting a counts request narrows to (mirrors server AudienceSelection).
export interface MarketingAudienceSelection {
  audience?: MarketingAudience;
  orgIds?: string[];
  includeContacts?: boolean;
  recipientEmails?: string[]; // hand-picked mode
}

// A row in the "Specific people" picker (accounts + external contacts).
export interface MarketingAudiencePerson {
  kind: 'user' | 'contact';
  email: string;
  name: string | null;
  unsubscribed: boolean;
}

export interface MarketingAudienceOrg { id: string; name: string; memberCount: number }

export interface MarketingContact {
  id: string;
  email: string;
  name: string | null;
  addedById: string;
  unsubscribedAt: string | null;
  createdAt: string;
}

export interface MarketingCampaignInput {
  name: string;
  subject: string;
  previewText?: string | null;
  mastheadTitle?: string | null;
  mastheadCadence?: string | null;
  mastheadIcon?: string | null;
  audience: MarketingAudience;
  orgIds?: string[];
  includeContacts?: boolean;
  recipientEmails?: string[];
  bodyJson: string;
}

// The hub's overview metrics board (GET /super-admin/marketing/metrics).
export interface MarketingMetrics {
  audience: MarketingAudienceCounts;
  today: { sent: number; cap: number };
  totals: {
    campaigns: number; sent: number; delivered: number; opened: number;
    clicked: number; bounced: number; complained: number; unsubscribed: number; failed: number;
  };
}

export const marketingApi = {
  metrics: () => api.get<MarketingMetrics>('/super-admin/marketing/metrics'),
  list:   () => api.get<MarketingCampaign[]>('/super-admin/marketing/campaigns'),
  get:    (id: string) => api.get<MarketingCampaign>(`/super-admin/marketing/campaigns/${id}`),
  create: (data: MarketingCampaignInput) => api.post<MarketingCampaign>('/super-admin/marketing/campaigns', data),
  update: (id: string, data: MarketingCampaignInput) => api.put<MarketingCampaign>(`/super-admin/marketing/campaigns/${id}`, data),
  remove: (id: string) => api.delete(`/super-admin/marketing/campaigns/${id}`),
  duplicate: (id: string) => api.post<MarketingCampaign>(`/super-admin/marketing/campaigns/${id}/duplicate`),
  audienceCounts: (sel?: MarketingAudienceSelection) =>
    api.get<MarketingAudienceCounts>('/super-admin/marketing/audience-counts', {
      params: {
        ...(sel?.audience ? { audience: sel.audience } : {}),
        ...(sel?.orgIds?.length ? { orgIds: sel.orgIds.join(',') } : {}),
        ...(sel?.includeContacts ? { includeContacts: '1' } : {}),
        ...(sel?.recipientEmails?.length ? { recipientEmails: sel.recipientEmails.join(',') } : {}),
      },
    }),
  audiencePeople: (q: string) =>
    api.get<MarketingAudiencePerson[]>('/super-admin/marketing/audience-people', { params: { q } }),
  audienceOrgs: () => api.get<MarketingAudienceOrg[]>('/super-admin/marketing/audience-orgs'),
  // The rolling external contact list.
  contacts:      () => api.get<MarketingContact[]>('/super-admin/marketing/contacts'),
  addContacts:   (text: string) => api.post<{ added: number; existing: number; alreadyUnsubscribed: number }>('/super-admin/marketing/contacts', { text }),
  removeContact: (id: string) => api.delete(`/super-admin/marketing/contacts/${id}`),
  // ONE renderer: previews are server-rendered so the iframe shows exactly what sends.
  previewDraft: (data: { subject: string; previewText?: string | null; bodyJson: string; mastheadTitle?: string | null; mastheadCadence?: string | null; mastheadIcon?: string | null }) =>
    api.post<{ subject: string; html: string }>('/super-admin/marketing/preview-draft', data),
  // Campaign image upload (the Image/screenshot block's "Upload" path) —
  // returns the permanent public URL the email will embed.
  uploadAsset: (data: { filename: string; mimeType: string; contentBase64: string }) =>
    api.post<{ id: string; url: string }>('/super-admin/marketing/assets', data),
  testSend: (id: string) => api.post<{ success: boolean; to: string }>(`/super-admin/marketing/campaigns/${id}/test-send`),
  send:     (id: string) => api.post<{ success: boolean; recipients: number; dailyCap: number }>(`/super-admin/marketing/campaigns/${id}/send`),
  schedule: (id: string, scheduledFor: string | null) => api.post<MarketingCampaign>(`/super-admin/marketing/campaigns/${id}/schedule`, { scheduledFor }),
  pause:    (id: string) => api.post<MarketingCampaign>(`/super-admin/marketing/campaigns/${id}/pause`),
  resume:   (id: string) => api.post<MarketingCampaign>(`/super-admin/marketing/campaigns/${id}/resume`),
};

// Shape of GET /super-admin/metrics (mirrors server lib/telemetry MetricsSnapshot).
export interface MetricsSnapshot {
  uptimeSec: number;
  memory: { current: { rssMb: number; heapUsedMb: number; heapTotalMb: number } | null; peakRssMb: number; samples: number };
  requests: { tracked: number; routes: Array<{ route: string; count: number; slow: number; p50: number; p95: number; max: number }> };
  queries: { total: number; slow: number; slowThresholdMs: number; recent: Array<{ model: string; op: string; ms: number; at: number }> };
  thresholds: Record<string, number>;
  breaches: string[];
}

// Jira
export const jiraApi = {
  getConfig: () => api.get('/jira/config'),
  // Disconnect the connection (admin only). keepTickets=true converts imported
  // tickets to editable, detached Avium tickets; false removes them.
  disconnect: (keepTickets: boolean) => api.delete('/jira/config', { params: { keepTickets } }),
  saveConfig: (data: Record<string, unknown>) => api.post('/jira/config', data),
  testConnection: (data: Record<string, unknown>) => api.post('/jira/test', data),
  getProjects: () => api.get('/jira/projects'),
  getSyncedKeys: () => api.get('/jira/synced-keys'),
  sync: (projectKey: string) => api.post(`/jira/sync/${projectKey}`),
  // Standalone query pull — sync everything the saved scope JQL matches, across
  // whatever projects it touches (no per-project selection).
  syncQuery: () => api.post('/jira/sync-query'),
  // Preview what a filter would pull (count + projects + statuses) before syncing.
  previewScope: (scopeJql: string) => api.post('/jira/preview-scope', { scopeJql }),
  getStatuses: (projectKeys?: string[], detail?: boolean) => api.get('/jira/statuses', { params: { ...(projectKeys?.length ? { projectKeys: projectKeys.join(',') } : {}), ...(detail ? { detail: '1' } : {}) } }),
  previewImport: (projectKeys?: string[]) => api.post('/jira/preview-import', { projectKeys: projectKeys ?? [] }),
  getDbStatuses: (projectKeys?: string[]) => api.get('/jira/db-statuses', { params: projectKeys?.length ? { projectKeys: projectKeys.join(',') } : undefined }),
  modifiedCount: () => api.get('/jira/modified-count'),
  issueTypes: (projectKey?: string) => api.get('/jira/issue-types', { params: projectKey ? { projectKey } : undefined }),
  lastSync: () => api.get('/jira/last-sync'),
  // The read-only commit-the-plan review (AVM-277 State D): the staged changes,
  // each with its kind + a human before→after, plus which are in conflict.
  pushPreview: () => api.get('/jira/push-preview'),
  // Commit the plan. `resolutions` carry the keep-mine / take-theirs decisions
  // for any conflict; omitted → nothing conflicting is pushed (all held).
  syncModified: (resolutions?: { key: string; field: string; take: 'mine' | 'theirs' }[]) =>
    api.post('/jira/sync-modified', resolutions?.length ? { resolutions } : undefined),
  workflowPhases: () => api.get('/jira/workflow-phases'),
  writeBack: (data: { ticketId: string; type: 'status' | 'fields'; statusName?: string; fields?: Record<string, unknown> }) =>
    api.post('/jira/write-back', data),
};

// Azure DevOps (parallel to jiraApi; one active connection per org)
export const azureApi = {
  previewImport: () => api.post('/azure/preview-import'),
  // THE UNIT (RULE 24). Azure DevOps enforces none — "hours or days", never recorded — so we
  // ask, against their own estimate values, and never guess. Until they answer we read hours
  // and the surface says so; a silent 8x error is the failure we cannot recover from.
  getEffortUnit: () => api.get('/azure/effort-unit'),
  setEffortUnit: (body: { effortUnit: 'hours' | 'days'; hoursPerDay: number }) =>
    api.post('/azure/effort-unit', body),
  getConfig: () => api.get('/azure/config'),
  disconnect: (keepTickets: boolean) => api.delete('/azure/config', { params: { keepTickets } }),
  saveConfig: (data: { orgUrl: string; projects: string[]; scopeWiql?: string | null; pat?: string }) => api.post('/azure/config', data),
  modifiedCount: () => api.get('/azure/modified-count'),
  // The read-only commit-the-plan review (AVM-277 State D, Azure analog): the
  // staged changes, each with its kind + a human before→after, plus which are
  // in conflict. Mirrors jiraApi.pushPreview's shape.
  pushPreview: () => api.get('/azure/push-preview'),
  // Commit the plan. `resolutions` carry the keep-mine / take-theirs decisions
  // for any conflict; omitted → nothing conflicting is pushed (all held).
  syncModified: (resolutions?: { key: string; field: string; take: 'mine' | 'theirs' }[]) =>
    api.post('/azure/sync-modified', resolutions?.length ? { resolutions } : undefined),
  testConnection: (data?: { orgUrl?: string; projects?: string[]; pat?: string }) => api.post('/azure/test', data ?? {}),
  getProjects: (data?: { orgUrl?: string; pat?: string }) => api.post('/azure/projects', data ?? {}),
  getStates: (data?: { orgUrl?: string; pat?: string; projects?: string[] }) => api.post('/azure/states', data ?? {}),
  // Preview what a WIQL filter would pull (count + projects + states) before syncing.
  previewScope: (data: { scopeWiql: string; orgUrl?: string; pat?: string }) => api.post('/azure/preview-scope', data),
  sync: () => api.post('/azure/sync'),
};

// Asana (parallel to azureApi; one active connection per org). Workspace /
// project listing are POSTs so the not-yet-saved PAT rides in the body.
export const asanaApi = {
  previewImport: () => api.post('/asana/preview-import'),
  getConfig: () => api.get('/asana/config'),
  disconnect: (keepTickets: boolean) => api.delete('/asana/config', { params: { keepTickets } }),
  saveConfig: (data: { workspaceGid: string; workspaceName?: string; projects: Array<{ gid: string; name: string }>; scope?: { incompleteOnly?: boolean }; pat?: string }) =>
    api.post('/asana/config', data),
  modifiedCount: () => api.get('/asana/modified-count'),
  // The read-only commit-the-plan review (AVM-277 State D parity): the staged
  // changes, each with its kind + a human before→after, plus which are in conflict.
  pushPreview: () => api.get('/asana/push-preview'),
  // Commit the plan. `resolutions` carry the keep-mine / take-theirs decisions
  // for any conflict; omitted → nothing conflicting is pushed (all held).
  syncModified: (resolutions?: { key: string; field: string; take: 'mine' | 'theirs' }[]) =>
    api.post('/asana/sync-modified', resolutions?.length ? { resolutions } : undefined),
  testConnection: (data?: { pat?: string }) => api.post('/asana/test', data ?? {}),
  getWorkspaces: (data?: { pat?: string }) => api.post('/asana/workspaces', data ?? {}),
  getProjects: (data: { workspaceGid: string; pat?: string }) => api.post('/asana/projects', data),
  getSections: (data?: { pat?: string; projectGids?: string[] }) => api.post('/asana/sections', data ?? {}),
  sync: () => api.post('/asana/sync'),
};

// Phases
export const hierarchiesApi = {
  list: () => api.get('/hierarchies'),
  create: (name: string) => api.post('/hierarchies', { name }),
  rename: (id: string, name: string) => api.put(`/hierarchies/${id}`, { name }),
  delete: (id: string) => api.delete(`/hierarchies/${id}`),
  addLevel: (id: string, name: string, color?: string) => api.post(`/hierarchies/${id}/levels`, { name, color }),
  updateLevel: (id: string, levelId: string, data: { name?: string; color?: string }) => api.put(`/hierarchies/${id}/levels/${levelId}`, data),
  reorderLevels: (id: string, order: Array<{ id: string; order: number }>) => api.put(`/hierarchies/${id}/levels/reorder`, { order }),
  deleteLevel: (id: string, levelId: string) => api.delete(`/hierarchies/${id}/levels/${levelId}`),
  seedJiraDefaults: () => api.post('/hierarchies/defaults/jira'),
};

// Routes moved /phases → /statuses 2026-06-17 (AVM-017). The server keeps
// /api/phases mounted as a backward-compat alias for one release.
export const statusesApi = {
  list: () => api.get('/statuses'),
  // Phase B.0-iii — the setup wizard's explicitly-edited Map rows (each pins + audits).
  stageOverrides: (overrides: Array<{ name: string; stage: string }>) => api.post('/statuses/stage-overrides', { overrides }),
  create: (data: Record<string, unknown>) => api.post('/statuses', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/statuses/${id}`, data),
  reorder: (order: Array<{ id: string; order: number }>) => api.put('/statuses/reorder', { order }),
  delete: (id: string) => api.delete(`/statuses/${id}`),
  // Status Model 1.0 (AVM-159): the status→status remap API (getMappings /
  // saveMappings / mapStatus / unmapStatus / sourceStatuses) is retired —
  // native statuses pass through 1:1 as their own columns.
  unmappedCount: () => api.get('/statuses/unmapped-count'),
  sources: () => api.get('/statuses/sources'),
  // Whether the clear-all-columns reset is available (zero tickets + no Jira).
  // Drives showing the button only when it would actually run.
  cleanupEligibility: () => api.get('/statuses/cleanup-eligibility'),
  // One-time cleanup of leftover board columns in a truly-empty workspace
  // (admin only; server refuses unless zero tickets AND no Jira connected).
  cleanupEmpty: () => api.post('/statuses/cleanup-empty'),
};

// Department Allocations
export const allocationsApi = {
  list: (params?: Record<string, string>) => api.get('/allocations', { params }),
  summary: (params?: Record<string, string>) => api.get('/allocations/summary', { params }),
  create: (data: Record<string, unknown>) => api.post('/allocations', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/allocations/${id}`, data),
  delete: (id: string) => api.delete(`/allocations/${id}`),
};

// Dashboard
export const dashboardApi = {
  overview: (params?: Record<string, string>) => api.get('/dashboard/overview', { params }),
  wip: (params?: Record<string, string>) => api.get('/dashboard/wip', { params }),
  hoursByDept: (params?: Record<string, string>) => api.get('/dashboard/hours-by-department', { params }),
  hoursByResource: (params?: Record<string, string>) => api.get('/dashboard/hours-by-resource', { params }),
  throughput: (params?: Record<string, string>) => api.get('/dashboard/throughput', { params }),
  // The trailing weekly history behind the Cycle Time / WIP gauges, computed in
  // the CALLER'S filter scope (lib/flowBaselines) — takes the same filter params
  // as /throughput, because a baseline drawn from a different scope than the
  // value is not a baseline.
  flowBaselines: (params?: Record<string, string>) => api.get('/dashboard/flow-baselines', { params }),
  sprintSummary: () => api.get('/dashboard/sprint-summary'),
  avgVelocity: (params?: Record<string, string>) => api.get('/dashboard/avg-velocity', { params }),
  historicVelocity: () => api.get('/dashboard/historic-velocity'),
};

// Real-time sync (AVM-216 Phase B) — enable/disable the inbound doorbell
// webhook per source (admin + Enterprise; explicit consent dialog first).
export const realtimeApi = {
  enable:  (source: 'jira' | 'azure' | 'asana') => api.post(`/realtime/${source}`),
  disable: (source: 'jira' | 'azure' | 'asana') => api.delete(`/realtime/${source}`),
};

// Automations (AVM-217 Phase C) — org rules (admin, Enterprise) + personal
// notify-self alerts (any role). Vocabulary served by /meta.
export const automationsApi = {
  meta:   () => api.get('/automations/meta'),
  list:   () => api.get('/automations'),
  create: (data: Record<string, unknown>) => api.post('/automations', data),
  update: (id: string, data: Record<string, unknown>) => api.patch(`/automations/${id}`, data),
  remove: (id: string) => api.delete(`/automations/${id}`),
  fires:  (id: string) => api.get(`/automations/${id}/fires`),
  notifyingMe: () => api.get('/automations/notifying-me'),
};

// Auto-sync settings (AVM-196 Phase A) — Data Sources -> Sync settings. Per-source
// scheduled-pull opt-in; OFF by default, admin-controlled, pull-only.
export const syncSettingsApi = {
  get: () => api.get('/sync-settings'),
  update: (source: 'jira' | 'azure' | 'asana', data: { enabled?: boolean; intervalMin?: number }) =>
    api.patch(`/sync-settings/${source}`, data),
};

// Planning surfaces inside the Intelligence page (Sprint Matrix, Patterns, etc.).
// CARRYOVER (Phase 3.1) — the active sprint's unfinished work as a claim on the next sprint.
// Scoped by the same people/project filters as the Sprint Matrix; `byResource` overlays the
// next-sprint cells and `claims` drive the drill. Unit-aware; `remaining` is null (never 0) when
// a carried ticket has no estimate (RULE 24).
export const carryoverApi = {
  sprint: (params?: Record<string, string>) => api.get('/carryover/sprint', { params }),
  // Stage a `carry_forward` lever for one ticket (Capacity's Person grain
  // drill drawer, Work Center slice W3) — born 'accepted'; the Jira
  // sprint-field write itself waits for the user's explicit Apply.
  stageCarry: (body: {
    ticketId: string; targetSprintId: string;
    reassignToResourceId?: string | null; hoursRemaining?: number | null;
  }) => api.post('/carryover/stage-carry', body),
};

export const planningApi = {
  getSprints:          (params?: Record<string, string>) => api.get('/planning/sprints', { params }),
  createSprint:        (data: Record<string, unknown>) => api.post('/planning/sprints', data),
  updateSprint:        (id: string, data: Record<string, unknown>) => api.put(`/planning/sprints/${id}`, data),
  deleteSprint:        (id: string) => api.delete(`/planning/sprints/${id}`),
  // AVM-156 — re-pull every connector (the ended-sprint "Refresh from source" lever).
  refreshSource:       () => api.post('/planning/refresh-source'),
  getFixVersions:      (params?: Record<string, string>) => api.get('/planning/fix-versions', { params }),
  createFixVersion:    (data: Record<string, unknown>) => api.post('/planning/fix-versions', data),
  updateFixVersion:    (id: string, data: Record<string, unknown>) => api.put(`/planning/fix-versions/${id}`, data),
  deleteFixVersion:    (id: string) => api.delete(`/planning/fix-versions/${id}`),
  getAvailability:     (params: Record<string, string>) => api.get('/planning/availability', { params }),
  getTrends:           (params: Record<string, string>) => api.get('/planning/trends', { params }),
  getSprintComparison: (params: Record<string, string>) => api.get('/planning/sprint-comparison', { params }),
  getPatterns:         (params?: Record<string, string>) => api.get('/planning/patterns', { params }),
  getBacklog:          (params?: Record<string, string>) => api.get('/planning/backlog', { params }),
  getSprintMatrix:     (params?: Record<string, string>) => api.get('/planning/sprint-matrix', { params }),
  getDepartmentForecast: (params?: Record<string, string>) => api.get('/planning/department-forecast', { params }),
  // Departments & Teams manager view (Capacity Holistic Redesign, state A) —
  // the active-sprint rollup of the same per-person matrix, grouped by department.
  getDepartmentSummaries: (params?: Record<string, string>) => api.get('/planning/department-summaries', { params }),
  // The trust panel (Capacity Holistic Redesign, state H) — "how solid is
  // this page?" alongside Departments & Teams' own headline coverage.
  getCapacityTrust: (params?: Record<string, string>) => api.get('/planning/capacity-trust', { params }),
  // DEMAND vs SUPPLY — what a body of work COSTS, against what the team can absorb. The ERM
  // thesis on one screen, and the question no surface in the product could previously answer:
  // every Capacity row was a person or a department, so a leader could see Nick at 143% and
  // could NOT see that the Payments epic costs 1,240h against a team that supplies 900.
  getEpicForecast: (params?: Record<string, string>) => api.get('/planning/epic-forecast', { params }),
  // Work — the Capacity Holistic Redesign's demand view (state C). Reuses
  // the SAME demand engine as getEpicForecast; this endpoint only reshapes
  // it for the redesigned surface + carries the additive team/open-items
  // facts.
  getWorkDemand: (params?: Record<string, string>) => api.get('/planning/work-demand', { params }),
  // The What-If SCENARIO OVERLAY (state C, COMPLETION 4) — the SAME shape as
  // getWorkDemand, computed with `levers` applied first (B1/D7-committed: an
  // overlay on the one calculator, never a parallel path). Nothing persists;
  // the client diffs this response against the baseline for the purple
  // before→after treatment.
  getWorkDemandScenario: (
    // The optional id arrays are the Work view's FilterBar lens (2026-07-19)
    // — the scenario recomputes over the same scope as the baseline grid.
    data: { levers: ScenarioLever[]; departmentIds?: string[]; teamIds?: string[]; resourceIds?: string[]; projectIds?: string[] },
    params?: Record<string, string>,
  ) => api.post('/planning/work-demand/scenario', data, { params }),
  // Kanban Flow Capacity grid (Capacity Holistic Redesign, state G) — the
  // person × pace/WIP/aging/typical-finish grid for one flow team. `?teamId`
  // omitted → the server's own default (the first flow team by name).
  // AVM-332 SLICE 5: departmentIds/resourceIds/projectIds — the SAME
  // FilterBar lens every other capacity route takes (comma-joined ids) —
  // narrow which people rows render + which work counts (server: aa35f5f7).
  getFlowGrid: (params?: { teamId?: string; scope?: string; departmentIds?: string; resourceIds?: string; projectIds?: string }) =>
    api.get('/planning/flow-grid', { params }),
  // The person-grain SCENARIO OVERLAY (Capacity Center, AVM-332 SLICE 3) — the
  // SAME shape/param convention as getWorkDemandScenario, overlaying the SAME
  // canonical matrix + Monte-Carlo forecast the Person grain and the
  // confidence tile already read (server/src/lib/personScenario.ts). No
  // `projectIds`: PersonScenarioSchema is people-side only, deliberately not
  // project-scoped (server/src/routes/writeSchemas.ts). Nothing persists;
  // the client diffs this response against the baseline matrix for the
  // purple before→after treatment.
  personScenario: (
    data: {
      levers: PersonLever[]; departmentIds?: string[]; teamIds?: string[]; resourceIds?: string[];
      sprintId?: string;
      // The Capacity Center's Window control (2026-07-19 adversarial-review
      // fix) — re-centers the scenario's window (and, absent an explicit
      // `sprintId`, the confidence forecast's target) on this sprint,
      // exactly like getSprintMatrix's own `anchorSprintId`.
      anchorSprintId?: string;
    },
    params?: Record<string, string>,
  ) => api.post<PersonScenarioResponse>('/planning/person-scenario', data, { params }),
  // The drill drawer (Capacity Holistic Redesign, state D) — one drill pattern
  // for every committed/capacity number on the three redesigned Capacity views.
  // `projectIds` mirrors the calling view's own Project filter (same param
  // style as getSprintMatrix) — never a second, drawer-only filtering pass.
  getCapacityDrill: (params: { level: 'department' | 'team' | 'person'; id: string; sprintId: string; scope?: string; projectIds?: string }) =>
    api.get('/planning/capacity-drill', { params }),
  // The Universal Capacity Editor (Capacity Holistic Redesign, state E) — the
  // one payload CapacityEditorModal reads, from every entry point.
  getCapacityEditor: (params: { resourceId: string; sprintId?: string }) =>
    api.get('/planning/capacity-editor', { params }),
  setSprintCapacity:   (resourceId: string, sprintId: string, capacity: number) =>
    api.put('/planning/sprint-capacity', { resourceId, sprintId, capacity }),
  // The Planning Baseline editor (AVM-339, docs/design/baseline-editor-hifi.html) —
  // WORK-open (every authenticated org role, Brian's round-1 verdict: "allow
  // anyone can edit the planning field"). `value: null` clears the target
  // (still audited); the unit is resolved server-side from the org's active
  // metricUnit, never sent by the client.
  setDepartmentBaseline: (departmentId: string, value: number | null) =>
    api.put<{ id: string; unit: 'hours' | 'points'; value: number | null }>(`/planning/departments/${departmentId}/baseline`, { value }),
  setTeamBaseline: (teamId: string, value: number | null) =>
    api.put<{ id: string; unit: 'hours' | 'points'; value: number | null }>(`/planning/teams/${teamId}/baseline`, { value }),
  // Clear an explicit sprint-capacity override back to the computed number
  // (the Universal Capacity Editor's "Remove", 2026-07-18 follow-through).
  clearSprintCapacity: (resourceId: string, sprintId: string) =>
    api.delete('/planning/sprint-capacity', { data: { resourceId, sprintId } }),
  getCapacityForecastVsActual: (sprintId: string, params?: Record<string, string>) =>
    api.get('/planning/capacity-forecast-vs-actual', { params: { sprintId, ...params } }),
  getResourcePlanned: (resourceId: string, sprintId: string) =>
    api.get('/planning/resource-planned', { params: { resourceId, sprintId } }),
  // The moment-of-assignment over-allocation check (Capacity Holistic
  // Redesign, state F) — one candidate, one ticket, the same calculator every
  // other Capacity surface reads.
  assignmentCheck: (resourceId: string, ticketId: string) =>
    api.get<AssignmentCheckResult>('/planning/assignment-check', { params: { resourceId, ticketId } }),
};

// Avium Signals (engine) + Avium AI Briefing (Claude layer) endpoints.
export const intelligenceApi = {
  getInsights:   (params?: Record<string, string>) => api.get('/intelligence/insights', { params }),
  // AVM-172 — the work behind a velocity point. Fetched LAZILY on click: the list
  // spans the whole closed-sprint window and would otherwise put megabytes of JSON
  // on the shared /insights payload that ten other callers never read.
  velocityItems: (params?: Record<string, string>) => api.get('/intelligence/velocity-items', { params }),
  graph:         (params?: Record<string, string>) => api.get('/intelligence/graph', { params }),
  parents:       (params?: Record<string, string>) => api.get('/intelligence/parents', { params }),
  search:        (params?: Record<string, string>) => api.get('/intelligence/search', { params }),
  population:    (params?: Record<string, string>) => api.get('/intelligence/population', { params }),
  getReports:    (params?: Record<string, string>) => api.get('/intelligence/reports', { params }),
  aiStatus:      () => api.get('/intelligence/ai/status'),
  briefing:      (params?: Record<string, string>) => api.post('/intelligence/ai/briefing', {}, { params }),
  risk:          (params?: Record<string, string>) => api.post('/intelligence/ai/risk', {}, { params }),
  dataQuality:   (params?: Record<string, string>) => api.post('/intelligence/ai/data-quality', {}, { params }),
  reviewDraft:   (params?: Record<string, string>) => api.post('/intelligence/ai/review-draft', {}, { params }),
  reportsNarrative: (params?: Record<string, string>) => api.post('/intelligence/ai/reports-narrative', {}, { params }),
  ask:           (question: string, params?: Record<string, string>) =>
    api.post('/intelligence/ai/ask', { question }, { params }),
  // Explore (interactive pivot)
  exploreFields: (params?: Record<string, string>) => api.get('/intelligence/explore/fields', { params }),
  exploreAsk:    (question: string, params?: Record<string, string>) => api.post('/intelligence/explore/ask', { question }, { params }),
  exploreRun:    (spec: unknown, params?: Record<string, string>) => api.post('/intelligence/explore/run', { spec }, { params }),
  exploreViews:  (params?: Record<string, string>) => api.get('/intelligence/explore/views', { params }),
  saveView:      (name: string, spec: unknown, params?: Record<string, string>) => api.post('/intelligence/explore/views', { name, spec }, { params }),
  deleteView:    (id: string, params?: Record<string, string>) => api.delete(`/intelligence/explore/views/${id}`, { params }),
  // Phase 5 Slice 5a — Configurable Lead Time report + the ReportConfig store
  // that backs every configurable report (org defaults + per-user saved views).
  leadTime:           (params?: Record<string, string>) => api.get('/intelligence/reports/lead-time', { params }),
  effortImpact:       (params?: Record<string, string>) => api.get('/intelligence/reports/effort-impact', { params }),
  processVariants:    (params?: Record<string, string>) => api.get('/intelligence/reports/process-variants', { params }),
  sprintForecast:     (params?: Record<string, string>) => api.get('/intelligence/reports/forecast', { params }),
  // What-If Scenarios — baseline vs scenario forecast under interactive levers.
  whatIf:             (params?: Record<string, string>) => api.get('/intelligence/forecast/what-if', { params }),
  // Closed-loop "Apply the move" family — suggestions + actions behind the
  // drill panels. All Avium-only: ticket changes ride the normal PUT /tickets
  // push queue; the nudge emails the assignee (1/ticket/day, audit-logged).
  scopeTrim:          (params?: Record<string, string>) => api.get('/intelligence/scope-trim', { params }),
  // Re-baseline — the Avium-internal "set a date you can hit" overlay for a lost
  // milestone. Never written back to source; powers the honest CTA + notify step.
  getRebaseline:      (milestoneType: string, refId: string) =>
    api.get('/intelligence/rebaseline', { params: { milestoneType, refId } }),
  rebaseline:         (data: { milestoneType: string; refId: string; date: string; reason?: string }) =>
    api.post('/intelligence/rebaseline', data),
  dataQualityItems:   (type: string, params?: Record<string, string>) =>
    api.get('/intelligence/data-quality/items', { params: { type, ...(params ?? {}) } }),
  nudge:              (ticketKey: string, reason?: string, ageDays?: number | null) =>
    api.post('/intelligence/nudge', { ticketKey, reason, ageDays }),
  // Cumulative Flow Diagram series (Kanban) — lazy, fetched when the flow drill opens.
  cfd:                (scope?: 'demo') => api.get('/intelligence/cfd', { params: scope ? { scope } : {} }),
  // Notify the team — announce an applied move. `recipients` returns the tiered
  // default list (affected people + their team); `notifyTeam` emails the chosen
  // set a structured summary (real data only, audit-logged).
  notifyTeamRecipients: (affectedResourceIds: string[]) =>
    api.post('/intelligence/notify-team/recipients', { affectedResourceIds }),
  notifyTeam:         (data: { recipientResourceIds: string[]; moves: { ticketKey: string; fromName: string; toName: string }[]; note?: string; sprintName?: string }) =>
    api.post('/intelligence/notify-team', data),
  // Avium Agents — the lever (Phase 1–3). syncActionItems reconciles levers from
  // the current signals (returns the open list); getActionItems reads them;
  // resolveActionItem advances one lever's lifecycle — accepted/declined is the
  // user's decision (Phase 2); applied/failed is the OUTCOME of the drill's
  // existing Apply step (Phase 3), reported once a move actually lands.
  syncActionItems:    (params?: Record<string, string>) => api.post('/intelligence/action-items/sync', {}, { params }),
  getActionItems:     (params?: Record<string, string>) => api.get('/intelligence/action-items', { params }),
  resolveActionItem:  (id: string, status: 'accepted' | 'declined' | 'applied' | 'failed', failureReason?: string) =>
    api.patch(`/intelligence/action-items/${id}`, { status, ...(failureReason ? { failureReason } : {}) }),
  // Phase 2 — persisted blockers + ageDays (the blocker-aging signal's drill data).
  getBlockers:        (params?: Record<string, string>) => api.get('/intelligence/blockers', { params }),
  // Phase 5 Slice 5b — Time-in-Status (per-phase dwell) report. Same
  // configurability + tier story as Lead Time.
  timeInStatus:       (params?: Record<string, string>) => api.get('/intelligence/reports/time-in-status', { params }),
  // Phase 5 Slice 5c — Reopen Rate + Sprint Scope-Change. Both Team+ gated,
  // both configurable, both surface a complementary Insight card (sprint-creep
  // for scope changes; reopens contribute to quality-grade signals).
  reopenRate:         (params?: Record<string, string>) => api.get('/intelligence/reports/reopen-rate', { params }),
  sprintScopeChange:  (params?: Record<string, string>) => api.get('/intelligence/reports/sprint-scope-change', { params }),
  // Phase 5 Slice 5d — Flow Efficiency, Hand-off Cost, and the consolidated
  // dashboard-kpis endpoint (lead time + flow efficiency medians for the
  // Dashboard's top gauge row).
  flowEfficiency:     (params?: Record<string, string>) => api.get('/intelligence/reports/flow-efficiency', { params }),
  handOffCost:        (params?: Record<string, string>) => api.get('/intelligence/reports/handoff-cost', { params }),
  dashboardKpis:      (params?: Record<string, string>) => api.get('/intelligence/dashboard-kpis', { params }),
  // Rescaled KPI gauges (Phase 1) — raw daily-snapshot history for one or more
  // metric keys, so a gauge can tell whether its own trailing baseline (e.g. 8
  // weeks) is actually backed by that much real history for this org.
  getSignalHistory:   (params: { keys: string; days?: string }) => api.get('/intelligence/signal-history', { params }),
  // Phase 5 Slice 5e — Workflow Sankey (process-mining over status transitions)
  // + Ping-pong anti-pattern (A↔B status bouncing). Same configurability/tier
  // story as the rest of Phase 5.
  workflowSankey:     (params?: Record<string, string>) => api.get('/intelligence/reports/workflow-sankey', { params }),
  pingPong:           (params?: Record<string, string>) => api.get('/intelligence/reports/ping-pong', { params }),
  listReportConfigs:  (reportType: string, params?: Record<string, string>) =>
    api.get('/intelligence/report-configs', { params: { reportType, ...(params ?? {}) } }),
  createReportConfig: (reportType: string, name: string, config: unknown) =>
    api.post('/intelligence/report-configs', { reportType, name, config }),
  updateReportConfig: (id: string, patch: { name?: string; config?: unknown }) =>
    api.put(`/intelligence/report-configs/${id}`, patch),
  deleteReportConfig: (id: string) =>
    api.delete(`/intelligence/report-configs/${id}`),
  // Admin-only — non-admins get 403 with a helpful error pointing them at
  // saving a personal view instead.
  setReportConfigDefault: (reportType: string, config: unknown) =>
    api.put(`/intelligence/report-configs/default/${reportType}`, { config }),
  // Phase 5 Slice 5f — admins can clear the org default; resolution falls back
  // to the product default after this (per-user views are untouched).
  clearReportConfigDefault: (reportType: string) =>
    api.delete(`/intelligence/report-configs/default/${reportType}`),
  getProductDefault:  (reportType: string) =>
    api.get(`/intelligence/report-configs/product-default/${reportType}`),
};

// Admin
export const adminApi = {
  // THE ORG'S STANDARD DAY (AVM-386) — how long a working day is here, and
  // which weekdays this org works. The default every person inherits, and the
  // thing every date-range capacity number is derived from. Admin-tier: an
  // org-wide setting, not a per-person planning edit.
  getOrgStandardDay: () => api.get('/admin/org/standard-day'),
  setOrgStandardDay: (data: { hoursPerDay?: number; workingDays?: number[]; effectiveFrom?: string }) =>
    api.put('/admin/org/standard-day', data),

  getDepartments: () => api.get('/admin/departments'),
  createDepartment: (data: Record<string, unknown>) => api.post('/admin/departments', data),
  updateDepartment: (id: string, data: Record<string, unknown>) => api.put(`/admin/departments/${id}`, data),
  deleteDepartment: (id: string) => api.delete(`/admin/departments/${id}`),

  getTeams: () => api.get('/admin/teams'),
  createTeam: (data: Record<string, unknown>) => api.post('/admin/teams', data),
  updateTeam: (id: string, data: Record<string, unknown>) => api.put(`/admin/teams/${id}`, data),
  deleteTeam: (id: string) => api.delete(`/admin/teams/${id}`),

  // FIELD MAPPING — "these are the fields Avium needs; point us at yours."
  // Avium PROPOSES; the customer CONFIRMS (RULE 24). `confirm` is the only call that makes
  // a mapping real; `preview` is what lets them agree against their own rows, not our label.
  discoverFields: (source = 'jira') => api.get(`/admin/field-mapping/discover?source=${source}`),
  // THE v5 CONTRACT (signal-first page, hi-fi approved 2026-07-17) — concepts grouped by
  // the Signals they unlock, server-type-gated candidates, live registry counts. Jira only
  // in phase 1; other sources 400 honestly and stay on the receipt view.
  signalRequirements: (source = 'jira') => api.get(`/admin/field-mapping/requirements?source=${source}`),
  // THE RECEIPT (AVM-284) — every native field Avium reads on this source, walkable back
  // to the customer's own rows, plus this source's one-time acknowledgment (if any).
  fieldMappingReceipt: (source = 'jira') => api.get(`/admin/field-mapping/receipt?source=${source}`),
  previewFieldMapping: (sourceFieldId: string, requirementId: string, source = 'jira') =>
    api.get(`/admin/field-mapping/preview?sourceFieldId=${encodeURIComponent(sourceFieldId)}&requirementId=${encodeURIComponent(requirementId)}&source=${encodeURIComponent(source)}`),
  confirmFieldMapping: (data: Record<string, unknown>) => api.post('/admin/field-mapping/confirm', data),
  removeFieldMapping: (id: string) => api.delete(`/admin/field-mapping/${id}`),
  // "We don't track this" — the first-class blameless decline (reversible, audited).
  // `acknowledged` is required by the server whenever the requirement's darkensSignals
  // is non-empty (else 400 { error: 'acknowledgment_required' }) — the DeclineAcknowledgeModal
  // (State D) is what supplies it.
  declineRequirement: (data: { source: string; requirementId: string; declined: boolean; acknowledged?: boolean }) =>
    api.post('/admin/field-mapping/decline', data),
  // The source-parity acknowledgment (State E) — "this source can't feed these signals,"
  // disclosed once at connection.
  acknowledgeSourceLimits: (source: string) => api.post('/admin/field-mapping/acknowledge-source-limits', { source }),
  // The release flow's step-3 preview: what releases WOULD this field create? Dry-run.
  releasePreview: (sourceFieldId: string, dateFieldId?: string | null) =>
    api.get(`/admin/field-mapping/release-preview?sourceFieldId=${encodeURIComponent(sourceFieldId)}${dateFieldId ? `&dateFieldId=${encodeURIComponent(dateFieldId)}` : ''}`),
  suggestFieldMappings: (source = 'jira') => api.post(`/admin/field-mappings/ai-suggest?source=${source}`),

  getImportFields: () => api.get('/import/fields'),
  // Phase 2c: a staged import passes a `token` (rows in R2) instead of the full
  // `rows` array, so a 100K file never round-trips through the browser.
  collisionCheck: (mapping: Record<number, string>, rows: string[][], token?: string | null) =>
    api.post('/import/collision-check', token ? { mapping, token } : { mapping, rows }),
  // Read-only: what taxonomy (departments/teams/projects/sprints/people) this
  // import would CREATE — shown on the final check so a wrong column mapping is
  // caught before anything is written. stageChoices = the wizard's three-bucket
  // classification for NEW statuses, so the completion-date disclosure
  // (Completion Model 1.0) classifies done rows exactly as the import will.
  importPreview: (mapping: Record<number, string>, rows: string[][], token?: string | null, stageChoices?: Record<string, string>) =>
    api.post('/import/preview', { mapping, ...(token ? { token } : { rows }), ...(stageChoices && Object.keys(stageChoices).length ? { stageChoices } : {}) }),
  // Distinct values of the mapped status column (server-side for staged imports,
  // since the browser only holds a preview).
  detectStatuses: (mapping: Record<number, string>, token: string) =>
    api.post('/import/statuses', { mapping, token }),

  getBranding: () => api.get('/admin/branding'),
  saveBranding: (data: Record<string, unknown>) => api.post('/admin/branding', data),
  saveBrandingBulk: (configs: unknown[]) => api.put('/admin/branding/bulk', { configs }),

  // AI usage + budget (cost control)
  getAiUsage: () => api.get('/admin/ai-usage'),
  setAiUsageSettings: (data: { aiMonthlyBudgetOverride?: number | null; aiUserDailyCapOverride?: number | null }) =>
    api.put('/admin/ai-usage/settings', data),

  // Avium API keys (Enterprise). The plaintext secret is returned by create +
  // rotate ONLY and shown to the admin exactly once.
  listApiKeys: () => api.get('/admin/api-keys'),
  createApiKey: (data: { name: string; scopes: string[]; expiresAt: string; rateLimitPerMin?: number | null; writeRateLimitPerMin?: number | null }) =>
    api.post('/admin/api-keys', data),
  // Edit name + rate limits in place (no new secret). Scopes are not editable here.
  updateApiKey: (id: string, data: { name?: string; rateLimitPerMin?: number | null; writeRateLimitPerMin?: number | null }) =>
    api.patch(`/admin/api-keys/${id}`, data),
  rotateApiKey: (id: string) => api.post(`/admin/api-keys/${id}/rotate`),
  revokeApiKey: (id: string) => api.delete(`/admin/api-keys/${id}`),

  getCustomFields: () => api.get('/admin/custom-fields'),
  createCustomField: (data: Record<string, unknown>) => api.post('/admin/custom-fields', data),
  updateCustomField: (id: string, data: Record<string, unknown>) => api.put(`/admin/custom-fields/${id}`, data),
  deleteCustomField: (id: string) => api.delete(`/admin/custom-fields/${id}`),

  getUsers: () => api.get('/admin/users'),
  createUser: (data: Record<string, unknown>) => api.post('/admin/users', data),
  updateUser: (id: string, data: Record<string, unknown>) => api.put(`/admin/users/${id}`, data),
  resetUserPassword: (id: string, newPassword: string) => api.put(`/admin/users/${id}/reset-password`, { newPassword }),
  deleteUser: (id: string) => api.delete(`/admin/users/${id}`),
};

// Demo data — any authenticated user in the org can manage demo data
export const demoDataApi = {
  status: () => api.get<DemoStatusResponse>('/demo-data/status'),
  // force=true is an explicit "Reload demo data" — re-seeds even when demo data
  // already exists (the server self-heals first), re-stamping the sample sprints
  // to the current date so a stale active sprint can't strand the Brief at 0 signals.
  seed:   (opts?: { force?: boolean }) => api.post('/demo-data/seed', opts?.force ? { force: true } : {}),
  delete: () => api.delete('/demo-data'),
};

export const stripeApi = {
  checkout: (tier: 'team' | 'business', seats?: number) => api.post('/stripe/checkout', { tier, seats }),
  portal: () => api.post('/stripe/portal'),
};

// The Law 6 sign-off ledger (docs/signoff/) — admin-only, served from the
// running app so it's a real HTTPS origin (the microphone works for voice
// sign-off; the Claude-artifact sandbox blocks it), rebuilt on every deploy,
// and independent of any Claude session or editor window. Returns a raw HTML
// string (rendered full-viewport in an iframe by QaLedgerPage), not JSON.
export const qaApi = {
  getLedger: () => api.get<string>('/qa'),
};
